import { lazy, Suspense } from 'react';
import { Link, Route, Routes } from 'react-router';

import { BrandLogo } from '@/componentes/BrandLogo';
import { BotonWhatsappConsultas } from '@/componentes/BotonWhatsappConsultas';
import { PanelErrorConfiguracion } from '@/componentes/PanelErrorConfiguracion';
import type { ConfiguracionApp, ErrorConfiguracion } from '@/configuracion/entorno';
import type * as EntradaPanelModulo from '@/panel/EntradaPanel';
import { ReservaPublica } from '@/publico/ReservaPublica';
import type { Resultado } from '@padel/domain';

let promesaModuloPanel: Promise<typeof EntradaPanelModulo> | null = null;

function cargarModuloPanel(): Promise<typeof EntradaPanelModulo> {
  promesaModuloPanel ??= import('@/panel/EntradaPanel');
  return promesaModuloPanel;
}

function anticiparModuloPanel(): void {
  void cargarModuloPanel();
}

const ModuloPanel = lazy(async () => {
  const modulo = await cargarModuloPanel();
  return { default: modulo.EntradaPanel };
});

interface PropiedadesApp {
  /**
   * La configuracion llega ya validada desde fuera, como `Resultado`. El componente no
   * lee `import.meta.env` por su cuenta: asi se puede montar en pruebas con cualquier
   * configuracion, valida o invalida, sin tocar el entorno.
   */
  readonly configuracion: Resultado<ConfiguracionApp, ErrorConfiguracion>;
}

function SitioPublico({ configuracion }: Pick<PropiedadesApp, 'configuracion'>): React.JSX.Element {
  return (
    <>
      <a className="saltar-al-contenido" href="#contenido">
        Saltar al contenido
      </a>

      <header className="encabezado-sitio">
        <div className="contenedor contenedor--ancho encabezado-sitio__contenido">
          <BrandLogo />
          <Link
            className="boton boton--secundario acceso-administrador"
            onFocus={anticiparModuloPanel}
            onPointerEnter={anticiparModuloPanel}
            onTouchStart={anticiparModuloPanel}
            to="/panel/iniciar-sesion"
          >
            Acceso administrador
          </Link>
        </div>
      </header>

      <main className="contenedor contenedor--ancho" id="contenido" tabIndex={-1}>
        {configuracion.ok ? (
          <Routes>
            <Route element={<ReservaPublica />} path="/" />
            <Route
              element={
                <>
                  <h1 className="titulo">Pagina no encontrada</h1>
                  <p>
                    La direccion solicitada no existe.{' '}
                    <a className="enlace" href="/">
                      Volver al inicio
                    </a>
                  </p>
                </>
              }
              path="*"
            />
          </Routes>
        ) : (
          <>
            <h1 className="titulo">Sistema de reservas de padel</h1>
            <PanelErrorConfiguracion error={configuracion.error} />
          </>
        )}
      </main>

      <footer className="pie-sitio">
        <div className="contenedor contenedor--ancho">
          <p>
            Elegí si pagás en efectivo o por transferencia. Las cancelaciones de reservas
            confirmadas tienen un cargo de $250.
          </p>
        </div>
      </footer>

      {configuracion.ok && configuracion.valor.whatsappConsultasNumero !== null ? (
        <BotonWhatsappConsultas numeroInternacional={configuracion.valor.whatsappConsultasNumero} />
      ) : null}
    </>
  );
}

function EntradaPanel({ configuracion }: Pick<PropiedadesApp, 'configuracion'>): React.JSX.Element {
  return (
    <Suspense
      fallback={
        <main className="contenedor contenedor--ancho" id="contenido-panel">
          <p aria-live="polite">Cargando panel…</p>
        </main>
      }
    >
      <ModuloPanel configuracion={configuracion} />
    </Suspense>
  );
}

/**
 * Estructura de la aplicacion: puntos de referencia semanticos y rutas.
 *
 * La raiz es **directamente el flujo de reserva**: no hay pagina comercial, ni registro,
 * ni login. Quien entra ve el dia, las dos canchas y sus horarios.
 *
 * La cabecera usa el unico logotipo oficial por medio de `BrandLogo`, que concentra la
 * ruta, el texto alternativo y las variantes de tamano permitidas.
 *
 * El enrutador NO se monta aca, sino en `main.tsx`. De ese modo las pruebas pueden
 * envolver `App` en un `MemoryRouter` y controlar la ruta.
 */
export function App({ configuracion }: PropiedadesApp): React.JSX.Element {
  return (
    <Routes>
      <Route element={<EntradaPanel configuracion={configuracion} />} path="/panel/*" />
      <Route element={<SitioPublico configuracion={configuracion} />} path="*" />
    </Routes>
  );
}
